import React from 'react';
import classnames from 'classnames';
import compact from 'lodash/compact';

import style from './FoldingComponent.module.scss';

import { Icon, IconType } from '@components/Icon';
import { ClickOutsideAlerter } from '@components/ClickOutsideAlerter';
import { RemoveButton, RemoveButtonModifiers } from '@components/ActionButtons/RemoveButton';
import { SaveButton } from '@components/ActionButtons/SaveButton';
import { CancelButton } from '@components/ActionButtons/CancelButton';

import { useFoldingComponent } from './hooks/useFoldingComponent';

interface TestingProps {
    controlDataTest?: string;
    additionalControlDataTest?: string;
    removeDataTest?: string;
}

export interface FoldingComponentProps extends TestingProps {
    title?: React.ReactNode;
    titleWithoutPadding?: boolean;
    unsetPosition?: boolean;
    content?: React.ReactNode;
    bottomLine?: React.ReactNode;
    additionalCardContent?: React.ReactNode;
    disableOpen?: boolean;
    children?: React.ReactNode;
    customCard?: React.ReactNode;
    withoutAnimation?: boolean;
    withoutBorder?: boolean;
    cancelButtonLabel?: string;
    isFavorite?: boolean;
    additionalControlButton?: {
        iconType: IconType;
        onClick(): void;
    };
    customControlButton?: {
        iconType: IconType;
        onClick(): void;
    };
    onRemoveClick?(): void;
    onSaveClick?(): void;
    onCancelClick?(): void;
    onContentClick?(event?: any): void;
    onFavoriteClick?(): void;
}

interface Props extends FoldingComponentProps {
    isOpen?: boolean;
    onOpenClick?(): void;
    onClickOutside?(event?: any): void;
}

export function FoldingComponent({
    title,
    titleWithoutPadding,
    content,
    bottomLine,
    unsetPosition,
    children,
    additionalCardContent,
    customControlButton,
    disableOpen,
    withoutAnimation,
    withoutBorder,
    customCard,
    cancelButtonLabel,
    additionalControlButton,
    isFavorite,
    controlDataTest,
    additionalControlDataTest,
    removeDataTest,
    onCancelClick,
    onRemoveClick,
    onSaveClick,
    onContentClick,
    onFavoriteClick,
    isOpen: isOpenProp,
    onOpenClick: onOpenClickProp,
    onClickOutside: onClickOutsideProp,
}: Props) {
    const { isOpen, maxHeight, cardRef, onOpenClick, onClickOutside } = useFoldingComponent({
        isOpen: isOpenProp,
        onOpenClick: onOpenClickProp,
        onClickOutside: onClickOutsideProp,
    });

    function getCard() {
        return customCard ? (
            customCard
        ) : (
            <React.Fragment>
                {title && title !== '' && (
                    <div className={classnames([style.title, titleWithoutPadding && style.title_withoutPadding])}>
                        {title}
                    </div>
                )}
                {content && content !== '' && (
                    <div
                        className={classnames([style.content, !!onContentClick && style.content_clickable])}
                        onClick={onContentClick}
                    >
                        {content}
                    </div>
                )}
                {bottomLine && bottomLine !== '' && <div className={style.bottomLine}>{bottomLine}</div>}
                {!!additionalCardContent && additionalCardContent}
                {!disableOpen ? (
                    <div className={style.controlButtons}>
                        <div
                            className={style.controlIcon}
                            data-test={controlDataTest}
                            data-testid={controlDataTest}
                            onClick={customControlButton ? customControlButton.onClick : onOpenClick}
                        >
                            <div className={style.iconWrapper}>
                                <Icon type={customControlButton ? customControlButton.iconType : IconType.EDIT} />
                            </div>
                        </div>
                        {onFavoriteClick && getFavoriteButton(onFavoriteClick, isFavorite)}
                        {additionalControlButton && (
                            <div
                                className={style.controlIcon}
                                data-test={additionalControlDataTest}
                                data-testid={additionalControlDataTest}
                                onClick={additionalControlButton.onClick}
                            >
                                <div className={style.iconWrapper}>
                                    <Icon type={additionalControlButton.iconType} />
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    onFavoriteClick && (
                        <div className={style.controlButtons}>{getFavoriteButton(onFavoriteClick, isFavorite)}</div>
                    )
                )}
            </React.Fragment>
        );
    }

    const actionButtons = compact([
        onSaveClick && (
            <SaveButton
                key={'FoldingComponent_SaveButton'}
                onSaveClick={onSaveClick}
            />
        ),
        onCancelClick && (
            <CancelButton
                key={'FoldingComponent_CancelButton'}
                onCancelClick={onCancelClick}
                label={cancelButtonLabel}
            />
        ),
        onRemoveClick && (
            <RemoveButton
                key={'FoldingComponent_RemoveButton'}
                dataTest={removeDataTest}
                onRemoveClick={onRemoveClick}
                modifiers={[RemoveButtonModifiers.PositionBottomRight]}
            />
        ),
    ]);

    return (
        <div
            className={classnames([
                style.root,
                isOpen && style.open,
                !!customCard && style.root_customCard,
                withoutAnimation && style.root_withoutAnimation,
                withoutBorder && style.root_withoutBorder,
                unsetPosition && style.root_unsetPosition,
            ])}
            style={{
                maxHeight: isOpen ? '1000px' : `${maxHeight}px`,
            }}
        >
            <div
                ref={cardRef}
                className={style.card}
            >
                {getCard()}
            </div>

            <div className={style.component}>
                <ClickOutsideAlerter onClickOutside={isOpen ? onClickOutside : undefined}>
                    <>
                        {children}
                        {actionButtons.length > 0 && <div className={style.actionButtons}>{actionButtons}</div>}
                    </>
                </ClickOutsideAlerter>
            </div>
        </div>
    );
}

function getFavoriteButton(onFavoriteClick?: () => void, isFavorite?: boolean) {
    return (
        <div
            className={style.controlIcon}
            onClick={onFavoriteClick}
        >
            <div className={style.iconWrapper}>
                <Icon type={isFavorite ? IconType.ADDED_FAVORITE : IconType.FAVORITE} />
            </div>
        </div>
    );
}
